// Ask Fleur — ch.06: "Read-only status assistant attached to the workspace …
// Bounded read tools, authorised records, evidence links and freshness. No
// write/payment tools, generated SQL or general-purpose agent chat."
//
// The demo's version of this is a chat drawer with a text box. This one is not,
// and the difference is the whole screen.
//
// ---------------------------------------------------------------------------
// There is no text box, and saying why is part of the screen
// ---------------------------------------------------------------------------
//
// ch.02: "Status questions use a bounded intent parser and server-side read
// tools (documentStatus, openWork, missingDocuments). No generated SQL,
// arbitrary URLs or posting tools."
//
// `POST /status-query` therefore takes an INTENT from a closed enum and refuses
// a body with anything else in it — the API sets `removeAdditional: false`
// exactly so a `question` field is answered with a 422 rather than silently
// dropped. The parser that would turn a typed sentence into one of three
// intents has not been built and has not been reviewed.
//
// So a text box here would have one of two futures, and both are dishonest. It
// could send the text and be refused, which is a control that never works. Or
// it could guess an intent in the browser from the words — which is an intent
// parser, unreviewed, in the place ch.04's privacy boundary least wants one, and
// it would answer a question the customer did not ask while looking like it
// understood them.
//
// What is here instead is three questions as three controls, one per read tool
// ch.02 names, and a line saying that free text is not accepted yet and why.
// A reviewer who wants a fourth question is asking for a fourth read tool, and
// that is a change to the API rather than something to type.
//
// ---------------------------------------------------------------------------
// The sentence is the server's, and it is not the authoritative part
// ---------------------------------------------------------------------------
//
// The answer string comes from a deterministic template in
// apps/api/routes/status-query.ts. This screen renders it, and renders the
// structured fields BESIDE it — the counts, the record links, the last
// successful sync — because those are what a reviewer should act on. ch.02:
// "Include record links and last sync time in answers"; contracts/http-api.md:
// "Dates and counts must come from authorised data, not language-model memory."
//
// Nothing on this screen is generated. There is no model in this path at all,
// and the screen says that too, because "AI assistant" invites the opposite
// assumption.
//
// ---------------------------------------------------------------------------
// What it will not answer about
// ---------------------------------------------------------------------------
//
// Every read happens in the verified tenant context of the selected office and
// under the selected administration filter. A document the session cannot see
// is a 404 identical to one that does not exist — ch.02's "queries about
// unauthorised administrations return no information, including no existence
// leak" — so this screen renders that absence as "not visible in this
// workspace" and never as "there is no such document".

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, request, tenantPath } from '../api/client.ts';
import type { StatusAnswer, StatusIntent, WorkItemKind } from '../api/types.ts';
import type { ScreenId } from '../components/Shell.tsx';
import { administrationFilter, ALL_ADMINISTRATIONS, useWorkspace } from '../state/workspace.tsx';
import * as format from '../format.ts';
import { Icon, type IconName } from '../components/Icon.tsx';

/**
 * The three read tools ch.02 names, as three questions.
 *
 * `needsDocument` is the one that takes a selector, and it is the reason this
 * is a list of objects rather than a list of strings: a question that needs an
 * identifier has to say so before it is asked, not fail afterwards.
 */
interface Question {
  readonly intent: StatusIntent;
  readonly label: string;
  readonly tool: string;
  readonly needsDocument: boolean;
}

const INTENT_ICONS: readonly IconName[] = ['list', 'mail', 'search'];

const QUESTIONS: readonly Question[] = [
  {
    intent: 'open_work',
    label: 'What is waiting for me?',
    tool: 'openWork',
    needsDocument: false,
  },
  {
    intent: 'missing_documents',
    label: 'What are we waiting on customers for?',
    tool: 'missingDocuments',
    needsDocument: false,
  },
  {
    intent: 'document_status',
    label: 'Where has one document got to?',
    tool: 'documentStatus',
    needsDocument: true,
  },
];

const COUNT_LABELS: Readonly<Record<WorkItemKind, string>> = {
  document: 'Documents to review',
  proposal: 'Proposals awaiting a decision',
  operation: 'Operations in flight',
  bank_match: 'Bank matches to decide',
  request: 'Open customer requests',
};

/** One exchange, kept so a reviewer can see what they asked as well as what came back. */
interface Turn {
  readonly id: number;
  readonly question: string;
  readonly answer: StatusAnswer | null;
  readonly error: string | null;
}

export interface AskFleurScreenProps {
  /** Opens a document the answer linked to. */
  readonly onOpenDocument: (documentId: string) => void;
  readonly onNavigate: (screen: ScreenId) => void;
}

export function AskFleurScreen({ onOpenDocument, onNavigate }: AskFleurScreenProps): ReactNode {
  const workspace = useWorkspace();
  const { token, administrationId } = workspace;
  const tenantId = workspace.membership.tenantId;

  const [turns, setTurns] = useState<readonly Turn[]>([]);
  const [documentId, setDocumentId] = useState('');
  const [busy, setBusy] = useState<StatusIntent | null>(null);
  // A counter rather than a timestamp: two answers within one millisecond is
  // two clicks, not an edge case, and Date.now() would give them the same key.
  const [nextTurn, setNextTurn] = useState(1);

  // An answer is a statement about ONE scope, and the header can change the
  // scope underneath this screen. An answer that survived that change would be
  // office A's counts sitting under office B's name, or the whole office's
  // numbers presented as one administration's — the same lie the Administrations
  // screen refuses to tell while a new office's list is in flight. So the
  // transcript is cleared rather than relabelled: what was asked was asked about
  // something that is no longer on screen.
  useEffect(() => {
    setTurns([]);
  }, [tenantId, administrationId]);

  const ask = useCallback(
    async (question: Question) => {
      const filter = administrationFilter(administrationId);
      setBusy(question.intent);
      const id = nextTurn;
      setNextTurn((n) => n + 1);
      try {
        const body = await request<StatusAnswer>(tenantPath(tenantId, '/status-query'), {
          method: 'POST',
          token,
          body: {
            intent: question.intent,
            // Only ever these. The request carries no text, and the API would
            // refuse a field it has not declared.
            ...(filter === undefined ? {} : { administrationId: filter }),
            ...(question.needsDocument ? { documentId: documentId.trim() } : {}),
          },
        });
        setTurns((previous) => [{ id, question: question.label, answer: body, error: null }, ...previous]);
      } catch (caught) {
        setTurns((previous) => [
          { id, question: question.label, answer: null, error: refusal(caught, question) },
          ...previous,
        ]);
      } finally {
        setBusy(null);
      }
    },
    [tenantId, token, administrationId, documentId, nextTurn],
  );

  const scope =
    administrationId === ALL_ADMINISTRATIONS
      ? 'every administration you can see'
      : (workspace.administrations.find((entry) => entry.id === administrationId)?.name ??
        'the selected administration');

  return (
    <>
      <div className="heading">
        <h1 className="with-mark">
          <span className="mark" aria-hidden="true">
            <Icon name="sparkle" size={18} />
          </span>
          Ask Fleur
        </h1>
        <p>
          Three questions answered from the records this session may read, in {scope}. Nothing here
          is written by a model, and nothing here writes anything.
        </p>
      </div>

      {/* The design's three intents, as cards. Each is one server-side read tool. */}
      <div className="intents">
        {QUESTIONS.map((question, index) => (
          <div key={question.intent} className="intent" data-testid={`question-${question.intent}`}>
            <span className="kic" aria-hidden="true">
              <Icon name={INTENT_ICONS[index] ?? 'list'} />
            </span>
            <div className="grow">
              <strong>{question.label}</strong>
              <p>
                Read tool <code className="code">{question.tool}</code>
              </p>
            </div>
            <button
              type="button"
              className="btn s"
              disabled={busy !== null || (question.needsDocument && documentId.trim() === '')}
              onClick={() => void ask(question)}
            >
              {busy === question.intent ? 'Asking…' : 'Ask'}
            </button>
          </div>
        ))}
      </div>

      <div className="grid2">
        <div className="card">
          <div className="card-head">
            <div>
              <h2>Ask about one document</h2>
              <p>The third question follows one document end to end</p>
            </div>
          </div>
          <div className="card-body">
            <label className="field" style={{ marginTop: 14 }}>
              Document id
              <input
                type="text"
                value={documentId}
                placeholder="00000000-0000-0000-0000-000000000000"
                onChange={(event) => setDocumentId(event.target.value)}
              />
            </label>
            <p className="muted small">
              {/* The same gap the Customer communication screen has for
                  attach-reply: there is no read that lists documents by
                  identifier for this purpose, so the id is typed or copied from
                  a screen that shows one. */}
              The third question needs a document. Copy an id from the work queue or the documents
              screen — there is no picker here, because no endpoint offers that list.
            </p>
          </div>
          <div className="card-foot">
            {/* The absence, stated where somebody would look for the box. */}
            There is no free-text box. The API takes one of three intents and refuses anything else,
            because the bounded intent parser ch.02 requires has not been built or reviewed. Guessing
            an intent from typed words in this browser would be that parser, unreviewed.
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <h2>Answers</h2>
              <p>Newest first</p>
            </div>
            {turns.length === 0 ? null : (
              <button type="button" className="btn text" onClick={() => setTurns([])}>
                Clear
              </button>
            )}
          </div>

          {turns.length === 0 ? (
            <div className="empty">
              <h3>Nothing asked yet</h3>
              <p>
                Pick one of the three questions. The answer is a sentence the server composed from a
                template, with the counts and the records it counted beside it.
              </p>
            </div>
          ) : (
            <div className="card-body">
              {turns.map((turn) => (
                <Answer
                  key={turn.id}
                  turn={turn}
                  onOpenDocument={onOpenDocument}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card section-space">
        <div className="card-head">
          <h2>What this assistant is, and is not</h2>
        </div>
        <div className="card-body">
          <ul className="note-list">
            <li>
              <b>No model is in this path.</b> The sentence comes from a deterministic template on
              the server. Nothing about the answer was generated, so there is nothing in it to
              hallucinate.
            </li>
            <li>
              <b>No write tools, ever.</b> ch.06: "No write/payment tools, generated SQL or
              general-purpose agent chat." The endpoint behind this screen is a read; it carries no
              idempotency key because it has nothing to make idempotent.
            </li>
            <li>
              <b>Only what you may already see.</b> The question runs in this office's verified
              context and under the administration filter in the header. A document another office
              holds is answered exactly as one that does not exist.
            </li>
            <li>
              <b>Only what we have synced.</b> Every answer carries the last successful sync and says
              it covers known records only. It does not claim to know what the accounting package has
              not told us.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

function Answer({
  turn,
  onOpenDocument,
  onNavigate,
}: {
  readonly turn: Turn;
  readonly onOpenDocument: (documentId: string) => void;
  readonly onNavigate: (screen: ScreenId) => void;
}): ReactNode {
  return (
    <div className="bubble" data-testid={`answer-${turn.id}`}>
      <p className="muted small">{turn.question}</p>
      {turn.error !== null ? (
        <p>{turn.error}</p>
      ) : turn.answer === null ? null : (
        <>
          <p>{turn.answer.answer}</p>

          {Object.entries(turn.answer.counts).length === 0 ? null : (
            <ul className="note-list">
              {Object.entries(turn.answer.counts).map(([kind, n]) => (
                <li key={kind}>
                  {COUNT_LABELS[kind as WorkItemKind] ?? kind}: <b>{n}</b>
                </li>
              ))}
            </ul>
          )}

          {turn.answer.sources.length === 0 ? null : (
            <div className="trace">
              {/* ch.02: "Include record links … in answers." A link the reviewer
                  can follow, not an identifier they have to carry. */}
              {turn.answer.sources.map((source) => (
                <p key={`${source.type}:${source.id}`}>
                  <b>{source.type}</b>{' '}
                  {source.type === 'document' ? (
                    <button
                      type="button"
                      className="table-action"
                      onClick={() => onOpenDocument(source.id)}
                    >
                      {source.id}
                    </button>
                  ) : source.type === 'request' ? (
                    <button type="button" className="table-action" onClick={() => onNavigate('messages')}>
                      {source.id}
                    </button>
                  ) : (
                    <span className="code">{source.id}</span>
                  )}
                </p>
              ))}
            </div>
          )}

          <p className="muted small">
            {/* Both halves of the freshness claim, always. A missing sync time is
                a fact, not a blank. */}
            {turn.answer.lastSuccessfulSyncAt === null
              ? 'No successful sync recorded for this scope.'
              : `Last successful sync ${format.dateTime(turn.answer.lastSuccessfulSyncAt)}.`}{' '}
            Covers known records only.
          </p>
        </>
      )}
    </div>
  );
}

/** The API's refusals, as sentences that do not overstate what is known. */
function refusal(error: unknown, question: Question): string {
  if (!(error instanceof ApiError)) return 'That question could not be answered.';
  if (error.isAbsent) {
    return question.needsDocument
      ? 'That document is not visible in this workspace. It may not exist, or it may belong to an administration you have not been granted — this answer is the same either way, on purpose.'
      : 'That office is not visible to this session.';
  }
  if (error.status === 422) {
    return 'That is not a question this endpoint accepts. Check the document id is a UUID.';
  }
  if (error.code === 'transport_error') {
    return 'The service could not be reached, so there is no answer — not an empty one.';
  }
  return 'That question could not be answered.';
}
